import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../../commons/abstract.service';
import { CopilotActionsEnvelope, CopilotActionsItemState } from '../../models/copilot-actions.model';
import { CopilotInsightEnvelope } from '../../models/copilot-insight.model';
import { CopilotAction, CopilotChatMessage } from './attendance-copilot-state.service';

export interface CopilotTriggerActionResponse {
  requestId: string;
  idAttendance: string;
  action: string;
  status: string;
}

export interface ChatHistoryQuery {
  limit?: number;
  before?: string;
}

export interface ChatHistoryResponse {
  data: CopilotChatMessage[];
  meta: { count: number; hasMore: boolean; nextCursor: string | null };
  error: string | null;
}

export interface CopilotModel {
  id: string;
  name: string;
  promptPrice?: string | null;
  completionPrice?: string | null;
  /** Set only on a free model of the Vercel AI Gateway catalog (RF-53). */
  free?: boolean;
}

export interface CopilotModelsResponse {
  data: CopilotModel[];
  meta: { count: number };
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class AttendanceCopilotApiService extends AbstractService<unknown> {

  getURLBase(): string {
    return '/copilot';
  }

  constructor(http: HttpClient) {
    super(http);
  }

  preCreate(_obj: unknown): void { }
  preUpdate(_obj: unknown): void { }

  triggerAction(
    idAttendance: string,
    action: CopilotAction,
    instruction?: string,
  ): Observable<CopilotTriggerActionResponse> {
    return super.post(`/attendances/${idAttendance}/actions`, { action, instruction });
  }

  getChatHistory(
    idAttendance: string,
    query: ChatHistoryQuery = {},
  ): Observable<ChatHistoryResponse> {
    const params: Record<string, string | number> = {};
    if (query.limit !== undefined) params['limit'] = query.limit;
    if (query.before) params['before'] = query.before;
    return super.getWithParams(`/attendances/${idAttendance}/chat-history`, params);
  }

  /** Curated catalog of selectable copilot models (GET /api/copilot/models). */
  listModels(): Observable<CopilotModelsResponse> {
    return super.get('/models');
  }

  /** The insight of a conversation (GET /api/copilot/attendances/:id/insight). */
  getInsight(idAttendance: string): Observable<CopilotInsightEnvelope> {
    return super.get(`/attendances/${idAttendance}/insight`);
  }

  /** Asks for a generation (POST /api/copilot/attendances/:id/insight/generate); answers the same view. */
  generateInsight(idAttendance: string): Observable<CopilotInsightEnvelope> {
    return super.post(`/attendances/${idAttendance}/insight/generate`, {});
  }

  /** The actions of a conversation (GET /api/copilot/attendances/:id/actions). */
  getActions(idAttendance: string): Observable<CopilotActionsEnvelope> {
    return super.get(`/attendances/${idAttendance}/actions`);
  }

  /** Marks one action (POST /api/copilot/attendances/:id/actions/items/:itemId/state); answers the same view. */
  markAction(idAttendance: string, itemId: string, state: CopilotActionsItemState): Observable<CopilotActionsEnvelope> {
    return super.post(`/attendances/${idAttendance}/actions/items/${itemId}/state`, { state });
  }

  /** Retries a failed generation, or asks for the first one (POST /api/copilot/attendances/:id/actions/generate); answers the same view. */
  generateActions(idAttendance: string): Observable<CopilotActionsEnvelope> {
    return super.post(`/attendances/${idAttendance}/actions/generate`, {});
  }
}
